import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthLayout from '../../components/layout/AuthLayout';
import { useGetProjectQuery } from '../../api/projects.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { PERMISSION_CONTACTS, PERMISSION_PROJECTS } from '../../utils/constants.js';
import IconMilestone from '../../components/images/IconMilestone.jsx';
import IconReport from '../../components/images/IconReport.jsx';
import IconContact from '../../components/images/IconContact.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import { useSelector } from 'react-redux';
import ModelShared from '../../components/misc/ModelShared.jsx';
import { formatNumbers } from '../../utils/formatNumbers.js';
import dayjs from 'dayjs';
import IconArrowDown from '../../components/images/IconArrowDown.jsx';
import { canUpdateOrDelete } from '../../utils/user.js';
import IconArrowRight from '../../components/images/IconArrowRight.jsx';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Badge } from 'primereact/badge';

const getStatusBadgeClass = (status) => {
    switch (status) {
        case 'planned':
            return 'bg-theme1-pink';
        case 'ongoing':
            return 'bg-theme1-info';
        case 'completed':
            return 'bg-theme1-success';
        case 'canceled':
            return 'bg-theme1-brightRed';
        default:
            return 'bg-gray-300';
    }
};

const ProjectDetail = () => {
    const { permissions: userPermissions } = useCurrentUser();
    const { id } = useParams();
    const navigate = useNavigate();
    const { data, isLoading } = useGetProjectQuery(id);
    const [isExpanded, setIsExpanded] = useState(false);
    const user = useSelector((state) => state.user.user);

    const status = {
        'to-do': { text: 'To-Do', color: 'bg-theme1-info' },
        'in-progress': { text: 'In Progress', color: 'bg-theme1-gold' },
        completed: { text: 'Completed', color: 'bg-theme1-success' }
    };

    const getStatus = (statusKey, date) => {
        const today = dayjs().startOf('day');
        const targetDate = dayjs(date).startOf('day');

        if (statusKey !== 'completed' && targetDate.isBefore(today)) {
            return { text: 'Overdue', color: 'bg-theme1-error' };
        }
        return status[statusKey] || 'Unknown';
    };

    const project = data?.response?.project;

    if (isLoading) {
        return (
            <AuthLayout title='Project Details' backRoute='/projects'>
                <ThreeDotsLoader className='mt-6' />
            </AuthLayout>
        );
    }

    if (!data) {
        return (
            <AuthLayout title='Project Details' backRoute='/projects'>
                <div className='text-center mt-6'>Project not found.</div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title='Project Details' backRoute='/projects'>
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-10'>
                <BaseCard className='py-6 px-6 bg-theme1-white'>
                    <div className='mb-5'>
                        <span
                            className={`ml-auto px-3 py-1 rounded-md text-[13px] font-bold text-white capitalize ${getStatusBadgeClass(project.status)}`}
                        >
                            {project.status}
                        </span>
                        <h4 className='font-bold text-[22px] text-theme1-title2 mb-0 mt-2'>{project?.name}</h4>
                        {project?.description && <div className='text-theme1-text2'>{project?.description}</div>}
                    </div>
                    {(project?.start_date || project?.end_date) && (
                        <div className='flex justify-between gap-4 py-3 border-b border-t border-solid border-theme1-tanTransparent'>
                            {project?.start_date && (
                                <div>
                                    <div className='text-[15px] text-theme1-title2 font-bold'>Start Date</div>
                                    <div className='text-[15px] text-theme1-title2'>
                                        {dayjs(project?.start_date).format('DD MMM YYYY')}
                                    </div>
                                </div>
                            )}
                            {project?.end_date && (
                                <div className='text-right'>
                                    <div className='text-[15px] text-theme1-title2 font-bold'>End Date</div>
                                    <div className='text-[15px] text-theme1-title2'>
                                        {dayjs(project?.end_date).format('DD MMM YYYY')}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {(project?.budget !== 0 || project?.actual_costs !== 0) && (
                        <>
                            <div className='flex justify-between gap-4 py-3 border-b border-solid border-theme1-tanTransparent'>
                                {project?.budget && (
                                    <div>
                                        <div className='text-[15px] text-theme1-title2 font-bold'>Budget</div>
                                        <div className='text-[24px] text-theme1-info'>
                                            {formatNumbers(project?.budget)}
                                        </div>
                                    </div>
                                )}

                                {project?.actual_costs && (
                                    <div className='text-right'>
                                        <div className='text-[15px] text-theme1-title2 font-bold'>Actual Cost</div>
                                        <div className='text-[24px] text-theme1-darkPink'>
                                            {formatNumbers(project?.actual_costs)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {project?.companies.length > 0 && (
                        <div className='flex justify-between items-center gap-4 py-3 border-b border-solid border-theme1-tanTransparent'>
                            <div className='flex-shrink-0'>
                                <div className='text-[15px] text-theme1-title2 font-bold'>Companies</div>
                            </div>
                            <div className='flex gap-1 flex-wrap'>
                                {project?.companies.map((company) => (
                                    <div
                                        key={company.id}
                                        className='text-xs font-bold text-theme1-text3 py-2 px-3 border border-solid border-theme1-text3 rounded-full'
                                    >
                                        <Link to={`/organizations/${company.organization_id}`}>{company.name}</Link>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {(project?.milestones?.length > 0 ||
                        project?.reports?.length > 0 ||
                        project?.contacts?.length > 0) && (
                        <div
                            className={`flex gap-2 mt-5 relative pb-10 ${isExpanded ? 'flex-col' : 'justify-between'}`}
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {project?.milestones?.length > 0 && (
                                <div className={`flex flex-col ${!isExpanded ? 'gap-1' : ''}`}>
                                    {isExpanded ? (
                                        <div className='mb-4'>
                                            <div className='flex items-center gap-2 mb-1'>
                                                <div className='rounded-full w-[35px] h-[35px] overflow-hidden flex items-center justify-center bg-theme1-gold'>
                                                    <IconMilestone className='fill-white w-5 h-5' />
                                                </div>
                                                <div className='text-base text-theme1-title2 font-bold'>Milestones</div>
                                            </div>
                                            <div className='text-sm text-theme1-title2 pl-[43px]'>
                                                {project.milestones.map((milestone, index) => (
                                                    <div key={milestone.id} className='text-sm text-theme1-title2 mb-1'>
                                                        <div className='font-bold flex'>
                                                            {index + 1}. {milestone.title}
                                                            <div
                                                                className={`w-min ml-1 px-3 rounded-md text-[13px] font-bold text-white capitalize ${getStatus(milestone.type, milestone.date).color}`}
                                                            >
                                                                {getStatus(milestone.type, milestone.date).text}
                                                            </div>
                                                        </div>
                                                        {milestone.description && (
                                                            <div className='text-theme1-text2 pl-4'>
                                                                {milestone.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className='flex items-center justify-center gap-2'>
                                                <div className='rounded-full w-[35px] h-[35px] overflow-hidden flex items-center justify-center bg-theme1-gold'>
                                                    <IconMilestone className='fill-white w-5 h-5' />
                                                </div>
                                                <div className='text-[26px] text-theme1-text1 font-extrabold'>
                                                    {project.milestones.length}
                                                </div>
                                            </div>
                                            <div className='text-sm text-theme1-title2 font-medium text-center'>
                                                Milestones
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {project?.reports?.length > 0 && (
                                <div className={`flex flex-col ${!isExpanded ? 'gap-1' : ''}`}>
                                    {isExpanded ? (
                                        <div className='mb-4'>
                                            <div className='flex items-center gap-2 mb-1'>
                                                <div className='rounded-full w-[35px] h-[35px] overflow-hidden flex items-center justify-center bg-theme1-goldDark'>
                                                    <IconReport className='fill-white w-5 h-5' />
                                                </div>
                                                <div className='text-base text-theme1-title2 font-bold'>
                                                    Progress Reports
                                                </div>
                                            </div>
                                            <div className='text-sm text-theme1-title2 pl-[43px]'>
                                                {project.reports.map((report, index) => (
                                                    <div key={report.id} className='text-sm text-theme1-title2 mb-1'>
                                                        <div className='font-bold'>
                                                            {index + 1}. {report.title}
                                                        </div>
                                                        {report.description && (
                                                            <div className='text-theme1-text2 pl-4'>
                                                                {report.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className='flex items-center justify-center gap-2'>
                                                <div className='rounded-full w-[35px] h-[35px] overflow-hidden flex items-center justify-center bg-theme1-goldDark'>
                                                    <IconReport className='fill-white w-5 h-5' />
                                                </div>
                                                <div className='text-[26px] text-theme1-text1 font-extrabold'>
                                                    {project.reports.length}
                                                </div>
                                            </div>
                                            <div className='text-sm text-theme1-title2 font-medium text-center'>
                                                Progress Reports
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {project?.contacts?.length > 0 && (
                                <div className={`flex flex-col ${!isExpanded ? 'gap-1' : ''}`}>
                                    {isExpanded ? (
                                        <>
                                            <div className='flex items-center gap-2 mb-1'>
                                                <div className='rounded-full w-[35px] h-[35px] overflow-hidden flex items-center justify-center bg-theme1-pale'>
                                                    <IconContact className='stroke-theme1-primary w-5 h-5' />
                                                </div>
                                                <div className='text-base text-theme1-title2 font-bold'>Key People</div>
                                            </div>
                                            <div className='text-sm text-theme1-title2 pl-[43px]'>
                                                {project.contacts.map((contact, index) => (
                                                    <div key={contact.id} className='text-sm text-theme1-title2 mb-1'>
                                                        <div className='font-bold flex'>
                                                            {index + 1}.
                                                            <Link
                                                                to={`/contacts/view/${contact.contact_id}`}
                                                                className='ml-1'
                                                            >
                                                                {[contact.first_name, contact.last_name]
                                                                    .filter(Boolean)
                                                                    .join(' ')}
                                                            </Link>
                                                            {contact.is_lead && (
                                                                <div
                                                                    className={`w-min ml-1 px-3 rounded-md text-[13px] font-bold text-white capitalize bg-theme1-info`}
                                                                >
                                                                    Lead
                                                                </div>
                                                            )}
                                                        </div>
                                                        {contact.email && (
                                                            <div className='text-theme1-text2 pl-4'>
                                                                {contact.email}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className='flex items-center justify-center gap-2'>
                                                <div className='rounded-full w-[35px] h-[35px] overflow-hidden flex items-center justify-center bg-theme1-pale'>
                                                    <IconContact className='stroke-theme1-primary w-5 h-5' />
                                                </div>
                                                <div className='text-[26px] text-theme1-text1 font-extrabold'>
                                                    {project.contacts.length}
                                                </div>
                                            </div>
                                            <div className='text-sm text-theme1-title2 font-medium text-center'>
                                                Key People
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            <IconArrowDown
                                className={`w-6 h-6 stroke-theme1-primary absolute bottom-0 left-1/2 transition-transform -ml-[12px] ${
                                    isExpanded ? 'rotate-180' : 'rotate-0'
                                }`}
                            />
                        </div>
                    )}
                </BaseCard>

                {project && (
                    <div className='max-w-3xl w-full mx-auto mt-4 mb-6'>
                        <div className='text-lg font-bold text-theme1-text2 mb-1'>
                            Shared within <span className='text-theme1-brightRed'>*</span>
                        </div>

                        <ModelShared
                            shared={project.shared}
                            sharedUsers={project.shared_users}
                            sharedOrganizations={project.shared_organizations}
                            user={user}
                            permission={PERMISSION_PROJECTS}
                        />
                    </div>
                )}
                <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 mt-0'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Reach</div>
                        {!!userPermissions[PERMISSION_PROJECTS]?.update &&
                            project &&
                            canUpdateOrDelete(user, project, PERMISSION_PROJECTS, 'update') && (
                                <div onClick={() => navigate(`/projects/edit/${id}`)}>
                                    <IconArrowRight className='w-8 h-8 stroke-theme1-title2' />
                                </div>
                            )}
                    </div>
                    <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='needs-tab'
                                    className={`custom-accordion-tab-no-padding ${(!project?.international && !project?.reach?.length) || !project?.reach?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Reach</span>
                                            <Badge
                                                value={
                                                    project?.international ? 'International' : project?.reach?.length
                                                }
                                                className='ns-badge'
                                            ></Badge>
                                        </>
                                    }
                                    disabled={
                                        (!project?.international && !project?.reach?.length) || !project?.reach?.length
                                    }
                                >
                                    {!project?.international && project?.reach?.length > 0 && (
                                        <div className='flex items-center flex-wrap'>
                                            {project?.reach &&
                                                project?.reach.map((region) => (
                                                    <span
                                                        key={region.id}
                                                        className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                    >
                                                        {region.name}
                                                    </span>
                                                ))}
                                        </div>
                                    )}
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                </BaseCard>

                {!!userPermissions[PERMISSION_CONTACTS]?.read && project?.files?.length > 0 && (
                    <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 max-w-3xl w-full mx-auto'>
                        <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                            <div className='text-2xl font-bold text-theme1-title2'>Files</div>
                        </div>
                        <BaseCard className='border-0 m-0 py-0 px-4'>
                            <div className='max-w-3xl w-full mx-auto px-4 pb-4'>
                                {project.files.map((file) => {
                                    return (
                                        <div key={file.id} className='w-full flex pt-4 items-center'>
                                            <a className='flex-grow text-sm text-theme1-text1' href={file.url}>
                                                {file.title}
                                            </a>
                                        </div>
                                    );
                                })}
                            </div>
                        </BaseCard>
                    </BaseCard>
                )}

                <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 mt-8 max-w-3xl w-full mx-auto'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Needs & Capabilities</div>
                        {!!userPermissions[PERMISSION_PROJECTS]?.update &&
                            project &&
                            canUpdateOrDelete(user, project, PERMISSION_PROJECTS, 'update') && (
                                <div onClick={() => navigate(`/projects/edit/${id}`)}>
                                    <IconArrowRight className='w-8 h-8 stroke-theme1-title2' />
                                </div>
                            )}
                    </div>
                    <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='needs-tab'
                                    className={`custom-accordion-tab-no-padding ${!project?.needs?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Needs</span>
                                            <Badge value={project?.needs?.length} className='ns-badge'></Badge>
                                        </>
                                    }
                                    disabled={!project?.needs?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {project?.needs &&
                                            project?.needs.map((need) => (
                                                <span
                                                    key={need.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {need.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                    <BaseCard className='border-0 m-0 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='capabilities-tab'
                                    className={`custom-accordion-tab-no-padding ${!project?.capabilities?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Capabilities</span>
                                            <Badge value={project?.capabilities?.length} className='ns-badge'></Badge>
                                        </>
                                    }
                                    disabled={!project?.capabilities?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {project?.capabilities &&
                                            project?.capabilities.map((capability) => (
                                                <span
                                                    key={capability.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {capability.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                </BaseCard>
                {!!userPermissions[PERMISSION_PROJECTS]?.update &&
                    project &&
                    canUpdateOrDelete(user, project, PERMISSION_PROJECTS, 'update') && (
                        <BaseButton className='btn-primary mt-10' onClick={() => navigate(`/projects/edit/${id}`)}>
                            Edit Project
                        </BaseButton>
                    )}
            </div>
        </AuthLayout>
    );
};

export default ProjectDetail;
